import React, { ReactElement, useEffect, useState } from 'react'
import TabTitle from './Tabs/TabTitle'
import { useSelector } from 'react-redux'
import { Role } from 'models/role'
import { isAllowed } from 'utils/helpers'
import TabContent from './Tabs/TabContent'

type Props = {
	children: ReactElement[]
	initialTab?: string
	tabMenuClassName?: string
	onTabChange?(tabKey: string): void
	hideTabContent?: boolean
	hideTabs?: Array<string>
	zIndex?: number
}

const Tabs: React.FC<Props> = ({
	initialTab,
	children,
	tabMenuClassName,
	onTabChange,
	hideTabContent = false,
	hideTabs = [],
	zIndex = 50,
}) => {
	const [selectedTab, setSelectedTab] = useState<string>(
		initialTab ? initialTab : children[0].props.tabKey
	)
	const user: any = useSelector((state) => state['auth'].user)

	const isAllowedToViewTab = (roles: Role[] | undefined) => {
		if (!roles) {
			return true
		}
		return isAllowed(user, roles)
	}

	const isNotAllowedToViewTab = (roles: Role[] | undefined) => {
		if (!roles) {
			return true
		}
		return !isAllowed(user, roles)
	}

	const handleTabChange = (tabKey: string) => {
		if (selectedTab !== tabKey) {
			setSelectedTab(tabKey)
			// Check if callback is defined
			if (onTabChange) {
				onTabChange(tabKey)
			}
		}
	}

	const filterNotAllowed = (setChildren) => {
		return (
			setChildren
				// Main isAllowed prop has preference over the roles filters
				.filter((item) =>
					item.props.isAllowed !== undefined ? item.props.isAllowed : true
				)
				.filter((item) => (hideTabs.includes(item.props.tabKey) ? false : true))
				.filter((item) => isAllowedToViewTab(item.props.allowedRoles))
				.filter((item) => isNotAllowedToViewTab(item.props.notAllowedRoles))
		)
	}

	useEffect(() => {
		setSelectedTab(initialTab)
	}, [initialTab])

	const allowedChildren = filterNotAllowed(children)

	const showActiveTabs = () => {
		if (hideTabContent) {
			return allowedChildren.map((c, i) => {
				let className = c.props.tabKey === selectedTab ? '' : 'hidden'
				return (
					<TabContent key={i} className={className}>
						{c}
					</TabContent>
				)
			})
		} else {
			// by default it will remove the whole content of the tab
			// resetting the components and removing them
			return (
				<TabContent>
					{allowedChildren.find((c) => c.props.tabKey === selectedTab)}
				</TabContent>
			)
		}
	}

	return (
		<div className="w-full">
			<div
				className={`mb-4 overflow-x-auto overflow-y-hidden bg-white top-0 sticky w-full pt-2 ${
					zIndex ? 'z-' + zIndex : ''
				}`}
			>
				<ul className={tabMenuClassName ?? 'flex px-6 border-b'}>
					{allowedChildren.map((item, index) => (
						<TabTitle
							key={index}
							isActive={selectedTab === item.props.tabKey}
							handleTabChange={handleTabChange}
							{...item.props}
						/>
					))}
				</ul>
			</div>
			{showActiveTabs()}
		</div>
	)
}

export default Tabs
